import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface HeroSectionProps {
  movies: Movie[];
  slides: { movieId: string; subtitle: string; ctaText: string }[];
}

export default function HeroSection({ movies, slides }: HeroSectionProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goToSlide((currentSlide + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [currentSlide, slides.length, goToSlide]);

  const currentMovie = movies.find((m) => m.id === slides[currentSlide].movieId);

  if (!currentMovie) return null;

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {slides.map((slide, index) => {
        const movie = movies.find((m) => m.id === slide.movieId);
        if (!movie) return null;
        return (
          <div
            key={slide.movieId}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={movie.backdrop}
              alt={movie.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        );
      })}

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1200px] mx-auto">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 text-xs font-medium rounded bg-accent-500 text-background-50">奇优推荐</span>
                <span className="text-sm text-foreground-800/80">⭐ {currentMovie.rating}</span>
              </div>
              <h2 className={`text-3xl md:text-5xl font-heading font-bold text-background-50 transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                {currentMovie.title}
              </h2>
              <p className={`mt-3 text-sm md:text-base text-foreground-700/90 leading-relaxed transition-all duration-500 delay-75 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                {slides[currentSlide].subtitle}
              </p>
              <div className={`flex items-center gap-3 mt-5 transition-all duration-500 delay-150 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                <Link
                  to={`/article/${currentMovie.slug}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-fill text-lg"></i>
                  {slides[currentSlide].ctaText}
                </Link>
                <Link
                  to={`/category/${currentMovie.categorySlug}`}
                  className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full border border-foreground-700/40 text-foreground-800 text-sm hover:border-primary-500/50 hover:text-primary-500 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-information-line"></i>
                  更多详情
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === currentSlide
                ? 'bg-primary-500 w-6'
                : 'bg-foreground-800/40 hover:bg-foreground-800/60'
            }`}
            aria-label={`第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}